import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  List,
  Anchor,
  SimpleGrid,
} from '@mantine/core';
import {
  IconInfoCircle,
  IconMapPin,
  IconStar,
  IconRoute,
  IconWheelchair,
  IconAlertTriangle,
  IconLink,
  IconToolsKitchen2,
  IconBuilding,
  IconBuildingStore,
  IconBuildingChurch,
  IconMovie,
  IconTent,
  IconMountain,
  IconWalk,
  IconCamera,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVITY_AI_DETAILS } from '@queries/activities';
import LoadingData from '@components/graphql-loading-data';
import GraphqlError from '@components/graphql-error';

interface ItineraryAttractionDetailProps {
  activityName: string;
}

type MoreInfo = {
  version: '1.0';
  activity_type: string;
  title: string;
  subtitle?: string;
  one_liner: string;
  hero: { image_query: string; icon: string; map_query: string };
  quick_facts: { label: string; value: string }[];
  blocks: Array<
    | { kind: 'narrative'; title: string; text: string }
    | { kind: 'highlights' | 'logistics' | 'accessibility' | 'safety'; title: string; items: string[] }
    | { kind: 'links'; title: string; items: { label: string; url: string }[] }
  >;
  confidence_notes?: string[];
};

function safeParseMoreInfo(input: unknown): { value?: MoreInfo; error?: string } {
  try {
    // AppSync AWSJSON often arrives as string; sometimes it’s already an object
    const obj = typeof input === 'string' ? JSON.parse(input) : input;

    if (!obj || typeof obj !== 'object') return { error: 'AI details are not a JSON object.' };

    // Minimal sanity checks (Zod on BE is the real guardrail)
    const anyObj = obj as Record<string, unknown>;
    if (anyObj.version !== '1.0') return { error: 'Unsupported AI details version.' };
    if (!anyObj.title || !anyObj.one_liner || !anyObj.hero || !Array.isArray(anyObj.blocks)) {
      return { error: 'AI details are missing required fields.' };
    }

    return { value: anyObj as MoreInfo };
  } catch {
    return { error: 'AI details are not valid JSON.' };
  }
}

function iconForActivity(key: string) {
  const k = (key || '').toLowerCase();
  if (k.includes('restaurant') || k.includes('food') || k.includes('dining')) return IconToolsKitchen2;
  if (k.includes('museum')) return IconBuilding;
  if (k.includes('church') || k.includes('cathedral')) return IconBuildingChurch;
  if (k.includes('movie') || k.includes('cinema')) return IconMovie;
  if (k.includes('camp')) return IconTent;
  if (k.includes('hike') || k.includes('trail') || k.includes('mountain')) return IconMountain;
  if (k.includes('walk')) return IconWalk;
  if (k.includes('shop') || k.includes('store') || k.includes('market')) return IconBuildingStore;
  if (k.includes('tour')) return IconCamera;
  return IconInfoCircle;
}

function blockMeta(kind: string) {
  switch (kind) {
    case 'highlights':
      return { icon: IconStar, label: 'Highlights' };
    case 'logistics':
      return { icon: IconRoute, label: 'Logistics' };
    case 'accessibility':
      return { icon: IconWheelchair, label: 'Accessibility' };
    case 'safety':
      return { icon: IconAlertTriangle, label: 'Safety' };
    case 'links':
      return { icon: IconLink, label: 'Links' };
    case 'narrative':
    default:
      return { icon: IconInfoCircle, label: 'Overview' };
  }
}

function ItineraryAttractionDetail({ activityName }: ItineraryAttractionDetailProps) {
  const { tripProfileId = '' } = useParams();

  const { data, loading, error } = useQuery(GET_ACTIVITY_AI_DETAILS, {
    variables: { tripProfileId, activityName },
    skip: !tripProfileId,
    fetchPolicy: 'cache-first',
  });

  const rawAiDetails = data?.getActivityAiDetails?.aiDetails;

  const parsed = useMemo(() => safeParseMoreInfo(rawAiDetails), [rawAiDetails]);

  if (loading)
    return (
      <LoadingData
        title={
          <>
            Fetching more details for:
            <br />
            {activityName}
          </>
        }
        titleOrder={3}
        blur={0}
      />
    );
  if (error) return <GraphqlError error={error} />;

  if (parsed.error || !parsed.value) {
    return (
      <Alert icon={<IconInfoCircle />} title="More info unavailable" color="yellow">
        <Text size="sm">{parsed.error ?? 'Unknown error.'}</Text>
        <Text size="sm" mt="xs" c="dimmed">
          Activity: {activityName}
        </Text>
      </Alert>
    );
  }

  const info = parsed.value;
  const ActivityIcon = iconForActivity(info.hero?.icon || info.activity_type);

  return (
    <Paper withBorder radius="md" p="md" style={{ height: '100%' }}>
      <ScrollArea h="100%">
        <Stack gap="md">
          {/* Header */}
          <Box>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Box>
                <Title order={4}>{info.title}</Title>
                {info.subtitle ? (
                  <Text c="dimmed" size="sm" mt={4}>
                    {info.subtitle}
                  </Text>
                ) : null}
              </Box>

              <ThemeIcon variant="light" size="lg" radius="md">
                <ActivityIcon />
              </ThemeIcon>
            </Group>

            <Text mt="sm">{info.one_liner}</Text>

            <Group mt="sm" gap="xs">
              <Badge variant="light">{info.activity_type}</Badge>
              {info.hero?.map_query ? (
                <Tooltip label="Open a Google Map" withArrow>
                  <Badge
                    variant="light"
                    leftSection={<IconMapPin size={14} />}
                    component="a"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.hero.map_query)}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open map in Google Maps"
                  >
                    Map available
                  </Badge>
                </Tooltip>
              ) : (
                <Badge variant="light" leftSection={<IconMapPin size={14} />}>
                  Map
                </Badge>
              )}
            </Group>
          </Box>

          <Divider />

          {/* Quick facts */}
          {Array.isArray(info.quick_facts) && info.quick_facts.length > 0 ? (
            <Box>
              <Text fw={600} mb="xs">
                Quick facts
              </Text>
              <SimpleGrid cols={2} spacing="sm">
                {info.quick_facts.slice(0, 6).map((f, idx) => (
                  <Paper key={`${f.label}-${idx}`} withBorder radius="sm" p="sm">
                    <Text size="xs" c="dimmed">
                      {f.label}
                    </Text>
                    <Text size="sm" fw={500}>
                      {f.value}
                    </Text>
                  </Paper>
                ))}
              </SimpleGrid>
            </Box>
          ) : null}

          {/* Blocks */}
          {info.blocks.map((b, idx) => {
            const meta = blockMeta(b.kind);
            const BlockIcon = meta.icon;

            return (
              <Box key={`${b.kind}-${idx}`}>
                <Group gap="xs" mb="xs">
                  <ThemeIcon variant="light" radius="md" size="md">
                    <BlockIcon size={16} />
                  </ThemeIcon>
                  <Text fw={600}>{b.title || meta.label}</Text>
                </Group>

                {b.kind === 'narrative' ? (
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {b.text}
                  </Text>
                ) : null}

                {b.kind === 'links' ? (
                  <List size="sm" spacing={6}>
                    {b.items.map((li, j) => (
                      <List.Item key={`${li.url}-${j}`}>
                        <Anchor href={li.url} target="_blank" rel="noreferrer">
                          {li.label}
                        </Anchor>
                      </List.Item>
                    ))}
                  </List>
                ) : null}

                {b.kind !== 'narrative' && b.kind !== 'links' ? (
                  <List size="sm" spacing={6}>
                    {b.items.map((it, j) => (
                      <List.Item key={`${j}-${it}`}>{it}</List.Item>
                    ))}
                  </List>
                ) : null}
              </Box>
            );
          })}

          {/* Confidence notes */}
          {info.confidence_notes?.length ? (
            <>
              <Divider />
              <Alert color="gray" title="Notes to verify" icon={<IconInfoCircle />}>
                <List size="sm" spacing={6}>
                  {info.confidence_notes.map((n, i) => (
                    <List.Item key={`${i}-${n}`}>{n}</List.Item>
                  ))}
                </List>
              </Alert>
            </>
          ) : null}
        </Stack>
      </ScrollArea>
    </Paper>
  );
}

export default ItineraryAttractionDetail;
